import {
  avatarAnatomy,
  datePickerAnatomy,
  dialogAnatomy,
  editableAnatomy,
  listAnatomy,
  listboxAnatomy,
  menuAnatomy,
  segmentGroupAnatomy,
  switchAnatomy,
  tableAnatomy,
  tabsAnatomy,
  tagAnatomy,
  toastAnatomy,
  tooltipAnatomy,
} from '@chakra-ui/react/anatomy';
import { ChakraV3 } from '@piccolohealth/ui';

export const inputRecipe = ChakraV3.defineRecipe({
  base: {},
  variants: {
    variant: {
      selectGhost: {
        rounded: 'md',
        border: 'none',
        bg: 'transparent',
        _hover: {
          bg: 'gray.100',
        },
        _focus: {
          bg: 'gray.100',
        },
        _focusVisible: {
          bg: 'gray.100',
        },
        _focusWithin: {
          bg: 'gray.100',
        },
        _expanded: {
          bg: 'gray.100',
        },
        _disabled: {
          opacity: '1',
        },
      },
      outline: {
        rounded: 'md',
      },
      flushed: {
        bg: 'transparent',
        rounded: 'none',
        borderTop: 'none',
        borderInline: 'none',
        borderBottom: '1px solid',
        borderColor: 'border',
        px: 0,
      },
    },
    size: {
      xs: {
        fontSize: 'xs',
        px: '2',
        '--input-height': 'sizes.6',
      },
      sm: {
        px: '2',
        '--input-height': 'sizes.8',
      },
      md: {
        px: '3',
        '--input-height': 'sizes.9',
      },
    },
  },
});

const buttonRecipe = ChakraV3.defineRecipe({
  base: {
    rounded: 'md',
  },
  variants: {
    variant: {
      selectGhost: {
        h: 'auto',
        minW: 'auto',
        px: 2,
        fontWeight: 'unset',

        _icon: { width: 4, height: 4 },
        _hover: {
          bg: 'gray.100',
          _disabled: { bg: 'unset', cursor: 'not-allowed' },
        },
        _active: { bg: 'gray.100' },
        _focus: { bg: 'gray.100' },
        _focusWithin: { bg: 'gray.100' },
        _focusVisible: { zIndex: 1, bg: 'gray.100' },
        _groupFocusWithin: { bg: 'gray.100' },
        _disabled: { opacity: 1, cursor: 'not-allowed' },
      },
      selectOutline: {
        minH: 8,
        h: 'auto',
        minW: 8,
        textStyle: 'sm',
        fontWeight: 'unset',
        px: 2,
        gap: 1,
        _icon: { width: 4, height: 4 },
        bg: 'white',
        borderWidth: '1px',
        borderColor: 'gray.200',
      },
      solid: {},
      outline: {},
      subtle: {
        color: 'black',
        bgColor: 'gray.100',
        _hover: {
          bgColor: 'gray.00',
        },
      },
      plain: {
        rounded: 'none',

        p: 0,
        minW: 0,
        h: 'auto',
        _hover: {
          textDecor: 'underline',
        },
        color: 'secondary',
        '&[data-active]': {
          bgColor: 'transparent',
        },
      },
      ghost: {
        _active: {
          bg: 'gray.200',
        },
      },
      surface: {},
    },
    // Requires a size variant to be defined before setting a default variant
    // Taken directly from ChakraV3 docs recipe for button
    size: {
      sm: {},
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const linkRecipe = ChakraV3.defineRecipe({
  base: {
    _focus: {
      outline: 'none',
    },
  },
});

const spinnerRecipe = ChakraV3.defineRecipe({
  base: {
    // TODO: Remove when we remove ChakraV2 theme from conflicting
    borderColor: 'unset !important',
    borderBottomColor: 'var(--spinner-track-color) !important',
  },
});

const tagSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: tagAnatomy.keys(),
  base: {},
  defaultVariants: {
    // @ts-expect-error  https://github.com/chakra-ui/chakra-ui/discussions/8518#discussioncomment-10918332
    // Bug in ChakraV3. Just ignoring for now
    variant: 'subtle',
  },
});

const tabsSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: tabsAnatomy.keys(),
  base: {
    root: {
      '--tabs-trigger-radius': 'radii.md',
    },
    trigger: {
      whiteSpace: 'nowrap',
    },
  },
});

export const tableSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: tableAnatomy.keys(),
  base: {
    root: {
      rounded: 'md',
    },
    caption: {
      px: 4,
      py: 2,
    },
  },

  variants: {
    size: {
      sm: {
        columnHeader: {
          px: 4,
        },
        cell: {
          px: 4,
          py: 2,
        },
      },
    },
  },
});

export const datePickerSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: datePickerAnatomy.keys(),
  base: {
    indicatorGroup: {
      opacity: 0.5,
    },
  },
  variants: {
    size: {
      xs: {
        root: {
          '--datepicker-input-height': 'sizes.6',
          '--datepicker-input-px': 'sizes.1.5',
          '--datepicker-indicators-offset': 'sizes.1.5',
        },
      },
      sm: {
        root: {
          '--datepicker-input-height': 'sizes.8',
          '--datepicker-input-px': 'sizes.2',
          '--datepicker-indicators-offset': 'sizes.2',
        },
      },
      md: {
        root: {
          '--datepicker-input-height': 'sizes.9',
          '--datepicker-input-px': 'sizes.3',
          '--datepicker-indicators-offset': 'sizes.3',
        },
      },
    },
    variant: {
      outline: {
        input: {
          bg: 'white',
        },
      },
    },
  },
});

const dialogSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: dialogAnatomy.keys(),
  base: {
    header: {
      p: 6,
      borderBottom: '1px solid',
      borderColor: 'gray.200',
    },
    body: {
      p: 6,
    },
    footer: {
      p: 6,
    },
    content: {
      borderRadius: 'xl',
    },
    closeTrigger: {
      border: 'none',
      _hover: {
        bg: 'transparent',
      },
    },
  },

  variants: {
    size: {
      cover: {
        body: {
          w: '95vw',
        },
        content: {
          maxW: '95vw',
          h: 'calc(95vh - 50px)',
        },
      },
      xl: {
        body: {
          w: '80rem',
        },
        content: {
          maxW: '80rem',
          minH: 'calc(95vh - 50px)',
        },
      },
      md: {
        body: {
          w: 'lg',
        },
      },
    },
  },
});

const switchSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: switchAnatomy.keys(),
  className: 'chakra-switch',
  base: {
    root: {
      colorPalette: 'brand',
    },
  },
});

const avatarSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: avatarAnatomy.keys(),
  className: 'chakra-avatar',
  base: {
    root: {
      // TODO: Remove when we remove ChakraV2 theme from conflicting
      borderColor: 'white !important',
    },
  },
});

const listSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: listAnatomy.keys(),
  base: {
    item: {
      _hover: {
        bg: 'gray.200 !important',
      },
      _selected: {
        bg: 'gray.200 !important',
      },
    },
  },
});

const editableSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: editableAnatomy.keys(),
  base: {
    preview: {
      _hover: { bg: 'transparent' },
    },
    control: {
      opacity: 0,
      _groupHover: { opacity: 1 },
      _groupFocusWithin: { opacity: 1 },
    },
  },
});

const menuSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: menuAnatomy.keys(),
  base: {
    content: {
      rounded: 'md',
    },
  },
});

const toastSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: toastAnatomy.keys(),
  base: {
    root: {
      rounded: 'lg',
      borderTopWidth: '4px',
      width: 'auto',
      minWidth: 'sm',
      maxWidth: 'lg',
      '&[data-type=success]': {
        bg: 'white',
        color: 'fg.success',
        borderTopColor: 'fg.success !important',
      },
      '&[data-type=error]': {
        bg: 'white',
        color: 'fg.error',
        borderTopColor: 'fg.error !important',
      },
      '&[data-type=info]': {
        bg: 'white',
        color: 'fg.info',
        borderTopColor: 'fg.info !important',
      },
      '&[data-type=warning]': {
        bg: 'white',
        color: 'fg.warning',
        borderTopColor: 'fg.warning !important',
      },
      '&[data-type=loading]': {
        bg: 'white',
        color: 'fg.info',
        borderTopColor: 'fg.info !important',
      },
    },
    actionTrigger: {
      borderColor: 'currentcolor',
      fontWeight: 'medium',
      color: 'inherit',
      cursor: 'button',
      '--toast-trigger-bg': 'color-mix(in srgb, currentcolor 8%, transparent)',
    },
  },
});

const listboxSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: listboxAnatomy.keys(),
  base: {
    content: {
      p: 0,
      borderWidth: 0,
    },
    item: {
      _highlighted: {
        bg: 'bg.muted',
        outline: 0,
      },
    },
    itemGroup: {
      m: 0,
    },
    itemGroupLabel: {
      color: 'secondary',
    },
  },
  variants: {
    variant: {
      plain: {
        content: {
          p: 0,
          borderWidth: '0',
        },
        item: {
          _hover: {
            bg: 'bg.emphasized/60',
          },
          _selected: {
            bg: 'bg.muted',
          },
        },
      },
    },
  },
});

export const segmentGroupSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: segmentGroupAnatomy.keys(),
  base: {
    root: {
      w: 'fit-content',
      overflow: 'hidden',
      rounded: 'md',
      _disabled: {
        opacity: 0.4,
      },
    },
    indicator: {
      bg: 'purple.500',
      color: 'white',
      borderRadius: 'none',
    },
    itemControl: {},
    item: {
      // First item: square off the right side
      '&:first-of-type:not(:last-of-type)': {
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '0px',
      },
      // Middle items: square off all sides
      '&:not(:first-of-type):not(:last-of-type)': {
        borderRadius: 'none',
      },
      // Last item: square off the left side
      '&:not(:first-of-type):last-of-type': {
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px',
      },
      _checked: {
        color: 'white',
      },
      _disabled: {
        cursor: 'not-allowed',
      },
    },
    itemText: {
      fontWeight: 'semibold',
    },
  },
  variants: {
    size: {
      xs: {
        item: {
          px: 2,
        },
      },
      sm: {
        item: {
          px: 3,
        },
      },
    },
  },
});

export const tooltipSlotRecipe = ChakraV3.defineSlotRecipe({
  slots: tooltipAnatomy.keys(),
  base: {
    content: {
      '--tooltip-bg': 'colors.gray.700',
      maxWidth: 'unset',
    },
  },
});

export const v3Theme = ChakraV3.createSystem(
  ChakraV3.defaultConfig,
  ChakraV3.defineConfig({
    cssVarsPrefix: 'v3',
    strictTokens: false,
    globalCss: {
      '*': {
        fontFeatureSettings: 'normal',
      },

      'html, body, #app': {
        fontSize: '14px',
        h: 'full',
        m: 0,
        p: 0,
        scrollBehavior: 'smooth',
      },
    },

    theme: {
      recipes: {
        button: buttonRecipe,
        input: inputRecipe,
        link: linkRecipe,
        spinner: spinnerRecipe,
      },
      slotRecipes: {
        avatar: avatarSlotRecipe,
        datePicker: datePickerSlotRecipe,
        dialog: dialogSlotRecipe,
        editable: editableSlotRecipe,
        list: listSlotRecipe,
        listbox: listboxSlotRecipe,
        menu: menuSlotRecipe,
        segmentGroup: segmentGroupSlotRecipe,
        switch: switchSlotRecipe,
        table: tableSlotRecipe,
        tabs: tabsSlotRecipe,
        tag: tagSlotRecipe,
        toast: toastSlotRecipe,
        tooltip: tooltipSlotRecipe,
      },

      layerStyles: {
        fill: {
          surface: {
            bg: 'gray.50',
          },
        },
      },
      semanticTokens: {
        colors: {
          brand: {
            solid: { value: '{colors.purple.500}' },
            contrast: { value: 'white' },
            fg: { value: '{colors.purple.700}' },
            muted: { value: '{colors.purple.100}' },
            subtle: { value: '{colors.purple.100}' },
            emphasized: { value: '{colors.purple.300}' },
            focusRing: { value: '{colors.purple.500}' },
          },
          secondary: {
            value: '#718096',
          },
          darkPurple: {
            value: '#805AD5',
          },
          extraDarkPurple: {
            value: '#00152A',
          },
          lightGray: {
            value: '#E8E8E8',
          },
          success: {
            value: '#38A169',
          },
          warning: {
            value: '#DD6B20',
          },
          danger: {
            solid: { value: '{colors.red.500}' },
            contrast: { value: 'white' },
            fg: { value: '{colors.red.700}' },
            muted: { value: '{colors.red.100}' },
            subtle: { value: '{colors.red.100}' },
            emphasized: { value: '{colors.red.300}' },
            focusRing: { value: '{colors.red.500}' },
          },
          purple: {
            // Values are from ChakraV2
            50: { value: '#FAF5FF' },
            100: { value: '#E9D8FD' },
            200: { value: '#D6BCFA' },
            300: { value: '#B794F4' },
            400: { value: '#9F7AEA' },
            500: { value: '#805AD5' },
            600: { value: '#6B46C1' },
            700: { value: '#553C9A' },
            800: { value: '#44337A' },
            900: { value: '#322659' },
            950: { value: '#201A39' },
          },
        },
        shadows: {},
      },
    },
  }),
);

// Make custom recipe variants typecheck. Recipe must list every valid variant
// (chakra defaults aren't merged in) — augmentation replaces, not extends.
declare module '@chakra-ui/react' {
  // @ts-expect-error - I don't have a good solution here. This is low impact, so just roll with it.
  interface ButtonProps {
    variant?: ChakraV3.RecipeVariantProps<typeof buttonRecipe>['variant'];
  }
  // @ts-expect-error - I don't have a good solution here. This is low impact, so just roll with it.
  interface InputProps {
    variant?: ChakraV3.RecipeVariantProps<typeof inputRecipe>['variant'] | 'subtle';
  }
}
